import clsx from 'clsx';
import { Formik, FormikProps } from 'formik';
import moment, { Moment } from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import ReCAPTCHA from 'react-google-recaptcha';

import Button from '@components/Button';
import { TextField } from '@components/FormFields';
import SectionTitle from '@components/SectionTitle';
import CustomSpinner from '@components/Spinner/CustomSpinner';
import { SubscriptionFormProps } from '@components/SubscriptionForm';
import { useSubscribeForm } from '@contexts/SubscribeFormContext';
import { useUserCreate } from '@hooks';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { RECAPTCHA_SITE_KEY } from '@src/env';
import { SelectedExtendedOffer } from '@utils/backend-api/subscribe';
import { LeadType } from '@utils/backend-api/users';
import { extractQueryParams } from '@utils/queryStringHelper';
import { YOUNG_PROFESSIONALS_PLAN } from '@utils/subscriptionCardOptions';

import validationSchema from './validationSchema';

interface FormValues {
  dob: Moment;
  email: string;
  name: string;
  userEmail?: string;
}

interface SignupFormProps extends Omit<SubscriptionFormProps, 'buttonText'> {
  formLocation: string;
  onFormSubmit?: () => void;
  selectedOffer: SelectedExtendedOffer;
}

const SignupForm = ({
  formLocation,
  onFormSubmit,
  selectedOffer: { code, isPro },
}: SignupFormProps) => {
  const {
    user: { dob, email, name },
    setUser,
  } = useSubscribeForm();
  const [isSignupFormSubmitted, setIsSignupFormSubmitted] = useState(false);
  const { mutateAsync: createUser, isLoading } = useUserCreate();
  const reCaptchaRef = useRef<ReCAPTCHA>();
  const formRef = useRef<FormikProps<FormValues>>();
  const { article_id: referringArticleId } = extractQueryParams(['article_id']);
  const isYPPlan = code.includes(YOUNG_PROFESSIONALS_PLAN);

  // Passed userEmail to the form for email validation initial state
  const initialState = useMemo(
    () => ({
      dob: dob ? moment(dob) : null,
      email: email || '',
      name: name || '',
      userEmail: email,
    }),
    [dob, email, name]
  );

  useEffect(() => {
    if (setIsSignupFormSubmitted) {
      if (email && name && ((isYPPlan && dob) || !isYPPlan)) {
        setIsSignupFormSubmitted(true);
      } else {
        setIsSignupFormSubmitted(false);
      }
    }
  }, [code, email, name, dob, isYPPlan, setIsSignupFormSubmitted]);

  const handleCreateUser = async ({ dob, email, name }: FormValues) => {
    if (isSignupFormSubmitted && onFormSubmit) {
      onFormSubmit();
      return;
    }
    const formattedDob = isYPPlan ? moment(dob.toString()).format('YYYY-MM-DD') : null;
    try {
      const refToken = await reCaptchaRef.current.executeAsync();
      const createUserParams = {
        dob: formattedDob,
        email,
        lead_type: LeadType.SUBSCRIBE_H_PAGE,
        form_location: formLocation,
        page: window.location.pathname,
        name,
        code,
        recaptcha_token: refToken,
        referring_article_id: referringArticleId,
      };
      const { stripeCustomerId } = await createUser(createUserParams);
      setUser({ dob: formattedDob, email, name, stripeCustomerId });
      setIsSignupFormSubmitted(true);
      if (onFormSubmit) {
        onFormSubmit();
      }
    } catch {
      setIsSignupFormSubmitted(false);
    } finally {
      reCaptchaRef.current.reset();
    }
  };

  return (
    <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} size="invisible" ref={reCaptchaRef}>
      <Formik
        enableReinitialize
        initialValues={initialState}
        onSubmit={handleCreateUser}
        validationSchema={validationSchema(isYPPlan)}
        innerRef={formRef}
      >
        {({ handleSubmit, errors, touched, values, setFieldValue }) => (
          <Form
            className="account-info mt-48 w-full sm:my-32 lg:my-64"
            noValidate
            onSubmit={handleSubmit}
          >
            <SectionTitle title={email ? 'Your account' : 'Create your account'} />
            <div className="flex flex-col items-start gap-8 sm:flex-row sm:gap-16">
              <TextField
                name="name"
                placeholder="Full Name"
                disabled={!!name}
                wrapperClassName="!mt-0 w-full flex-2"
              />
              <TextField
                name="email"
                placeholder="Email"
                disabled={!!email}
                wrapperClassName="!mt-0 w-full flex-2"
              />
              {isYPPlan && (
                <div className="basis-0 w-full flex-2 md:flex-1">
                  <DesktopDatePicker
                    disableFuture
                    disabled={!!dob}
                    maxDate={moment().subtract(13, 'years')}
                    minDate={moment().subtract(31, 'years')}
                    onChange={(value) => setFieldValue('dob', value, true)}
                    value={values.dob}
                    slotProps={{
                      textField: {
                        variant: 'outlined',
                        placeholder: 'Birthday',
                        size: 'small',
                        name: 'dob',
                        className: touched.dob && errors?.dob ? 'is-invalid' : '',
                      },
                    }}
                  />
                  {touched.dob && errors?.dob && (
                    <div className="mt-6 text-body-sm text-primary-red">
                      {errors.dob as unknown as string}
                    </div>
                  )}
                </div>
              )}
              <Button
                id="signupBtn"
                disabled={isLoading}
                type="submit"
                className={clsx('w-full !rounded-2 !capitalize sm:w-[130px] md:w-[248px]', {
                  '!border-pro-red !bg-pro-red': isPro,
                })}
              >
                {isLoading ? (
                  <CustomSpinner
                    color="#fff"
                    size={10}
                    style={{ display: 'flex', justifyContent: 'center' }}
                  />
                ) : (
                  <>Continue</>
                )}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </ReCAPTCHA>
  );
};

export default SignupForm;
